// Καθαρές συναρτήσεις των προκαταβολών ταμείου (τεστάρονται χωρίς οθόνη): ποσά σε ακέραια λεπτά,
// μορφή «2,60 €» όπως στο υπόλοιπο ταμείο, τι επιτρέπεται σε ποια κατάσταση.

import { formatEuroGr } from '../../utils/dineInBasketDiscount';
import type { DepositMethod, DepositView, FiscalRef } from './types';

/** Όριο του server (MAX_CENTS): 10.000 € ανά προκαταβολή. */
export const MAX_CENTS = 1000000;

/** Ποσό σε ευρώ (αριθμός ή string του pg numeric) → ακέραια λεπτά. */
export const toCents = (v: number | string | null | undefined) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** «2,60 €» — ίδια μορφή με το υπόλοιπο ταμείο. */
export const money = (euros: number | string | null | undefined) => formatEuroGr(toCents(euros));

/**
 * Ό,τι πληκτρολόγησε ο ταμίας («20», «20,5», «20.50») → λεπτά, ή null αν δεν είναι έγκυρο ποσό.
 * Ίδιος κανόνας με τον server (depositDocuments.toCents): έως 2 δεκαδικά, όχι αρνητικά.
 */
export function parseAmount(input: string): number | null {
  const s = String(input || '').trim().replace(/\s+/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [int, dec = ''] = s.split('.');
  return Number(int) * 100 + Number(`${dec}00`.slice(0, 2));
}

/** Λεπτά → κείμενο για το πεδίο ποσού («20,00»). */
export const centsToInput = (cents: number) => (Math.max(0, cents) / 100).toFixed(2).replace('.', ',');

/** Λεπτά → ό,τι στέλνουμε στον server («20.00»). */
export const centsToApi = (cents: number) => (cents / 100).toFixed(2);

export const digits = (s: string | null | undefined) => String(s || '').replace(/\D/g, '');

/** Ο server θέλει τουλάχιστον 10 ψηφία για να βρει/φτιάξει πελάτη. */
export const validPhone = (s: string) => digits(s).length >= 10;

/** «Α-12» — σειρά και αριθμός παραστατικού. */
export const docNumber = (r: Pick<FiscalRef, 'series' | 'number'> | null | undefined) =>
  (r ? [r.series, r.number].filter(x => x != null && String(x) !== '').join('-') : '');

/** Το 8.4 έχει MARK (πέρασε στην ΑΑΔΕ). */
export const hasMark = (r: FiscalRef | null | undefined) => !!(r && r.mark);

/** Ανοιχτή = υπάρχει υπόλοιπο για επιστροφή ή συμψηφισμό. */
export const isOpen = (d: Pick<DepositView, 'available'>) => toCents(d.available) > 0;

/** Θέλει κάτι από τον ταμία ή τον λογιστή: οφειλόμενη επιστροφή, εκκρεμής τακτοποίηση, 8.4 χωρίς MARK. */
export const needsAttention = (d: DepositView) =>
  d.refund_due === true || d.settlement === 'pending' || (d.method !== 'cash' && d.receipt_pending === true);

/** Ανοιχτές πρώτα (οφειλόμενες επιστροφές πρώτες απ' όλες), μετά οι νεότερες. */
export function sortDeposits(list: DepositView[]): DepositView[] {
  const rank = (d: DepositView) => (d.refund_due && isOpen(d) ? 0 : isOpen(d) ? 1 : d.settlement === 'pending' ? 2 : 3);
  return list.slice().sort((a, b) => rank(a) - rank(b) || String(b.created_at).localeCompare(String(a.created_at)) || b.id - a.id);
}

/** Προκαταβολή στην κράτηση: δεν υπάρχει ήδη ενεργή (ούτε online σε αναμονή). Ίδιο με τον server (resolveOwner). */
export const ACTIVE_RESERVATION_DEPOSIT = ['awaiting', 'paid', 'applied', 'forfeited'];

export function reservationTakesDeposit(r: { status: string; deposit_status?: string | null }) {
  return (r.status === 'pending' || r.status === 'confirmed') && !ACTIVE_RESERVATION_DEPOSIT.includes(String(r.deposit_status || ''));
}

/** Το ποσό που προτείνει η κράτηση (deposit_due), σε λεπτά — 0 αν δεν υπάρχει. */
export const dueCents = (r: { deposit_due?: number | string | null; deposit?: { due?: number | null } | null }) =>
  toCents(r.deposit?.due ?? r.deposit_due ?? 0);

// ─── Προκαταβολή πελάτη στην πληρωμή του Take Away (14/10/2026) ──────────────────────────────────

/** Ο server δέχεται έως 5 προκαταβολές σε μία παραγγελία (depositApplications.MAX_USES). */
export const MAX_DEPOSIT_USES = 5;

/** Μία αφαίρεση: από ποια προκαταβολή, πόσα λεπτά (ο τρόπος μόνο για την εμφάνιση). */
export interface DepositUse {
  paymentId: number;
  amountCents: number;
  method: DepositMethod;
}

export const sumCents = (uses: Array<{ amountCents: number }>) => uses.reduce((s, u) => s + u.amountCents, 0);

/**
 * Πόσο μπορεί να αφαιρεθεί από μία προκαταβολή: όσο έχει διαθέσιμο, χωρίς να ξεπεράσει — μαζί με τις
 * άλλες επιλεγμένες — το σύνολο της παραγγελίας. Ίδιοι κανόνες με τον server (applyToOrder).
 */
export const capFor = (availableCents: number, totalCents: number, othersCents: number) =>
  Math.max(0, Math.min(availableCents, totalCents - othersCents));

/** Το πεδίο depositUses της παραγγελίας: ποσό ως κείμενο με ακριβώς 2 δεκαδικά («200.00»). */
export const depositUsesBody = (uses: Array<Pick<DepositUse, 'paymentId' | 'amountCents'>>) =>
  uses.map(u => ({ paymentId: u.paymentId, amount: centsToApi(u.amountCents) }));

/** Κωδικοί του server που σημαίνουν «η επιλογή δεν ισχύει πια — διάλεξε ξανά» (το μήνυμά του στον ταμία). */
const RESELECT_CODES = new Set([
  'deposit_not_found', 'deposit_insufficient', 'deposit_exceeds_total', 'deposit_partial', 'deposit_other_customer',
  'deposit_duplicate', 'too_many_deposits', 'invalid_deposit', 'deposit_not_allowed', 'feature_disabled', 'counter_deposits_not_ready',
]);
/** Προσωρινά: η ίδια επιλογή περνά σε λίγο (επιστροφή σε εξέλιξη, 8.4 που δεν έφτασε στον πάροχο). */
const RETRY_CODES = new Set(['receipt_84_pending', 'refund_in_progress']);

export function depositErrorKind(code: string | null | undefined): 'reselect' | 'retry' | null {
  if (!code) return null;
  if (RESELECT_CODES.has(code)) return 'reselect';
  if (RETRY_CODES.has(code)) return 'retry';
  return null;
}
